import {
    RuleActionType,
    RuleConditionType,
} from "@common/types/transactionRule";

interface RuleEntitySource {
    conditions?: readonly unknown[] | null;
    actions?: readonly unknown[] | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toEntityId(value: unknown): number | null {
    if (typeof value === "number") {
        return Number.isInteger(value) ? value : null;
    }
    if (typeof value === "string" && value.length > 0) {
        const parsed = Number(value);
        return Number.isInteger(parsed) ? parsed : null;
    }
    return null;
}

function collectEntityIds(value: unknown, ids: Set<number>): void {
    if (value === null || value === undefined) {
        return;
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            collectEntityIds(item, ids);
        }
        return;
    }
    if (isRecord(value)) {
        const id = toEntityId(value.id);
        if (id !== null) {
            ids.add(id);
        }
        return;
    }
    const id = toEntityId(value);
    if (id !== null) {
        ids.add(id);
    }
}

function collectSplitEntityIds(value: unknown, ids: Set<number>): void {
    let parsed: unknown = value;
    if (typeof value === "string") {
        try {
            parsed = JSON.parse(value);
        } catch {
            return;
        }
    }
    if (!isRecord(parsed) || !Array.isArray(parsed.splits)) {
        return;
    }
    for (const split of parsed.splits) {
        if (isRecord(split)) {
            const id = toEntityId(split.entityId);
            if (id !== null) {
                ids.add(id);
            }
        }
    }
}

/**
 * Collects every entity id a rule references: the ENTITY condition scope, the
 * ENTITY action target, and SPLIT action per-line entity ids. Handles both raw
 * values (live rules) and resolved { id, name } values (audit snapshots).
 */
export function getRuleReferencedEntityIds({
    conditions,
    actions,
}: RuleEntitySource): number[] {
    const ids = new Set<number>();

    for (const condition of conditions ?? []) {
        if (
            isRecord(condition) &&
            condition.conditionType === RuleConditionType.ENTITY
        ) {
            collectEntityIds(condition.value, ids);
        }
    }

    for (const action of actions ?? []) {
        if (!isRecord(action)) {
            continue;
        }
        if (action.actionType === RuleActionType.ENTITY) {
            collectEntityIds(action.value, ids);
        } else if (action.actionType === RuleActionType.SPLIT) {
            collectSplitEntityIds(action.value, ids);
        }
    }

    return Array.from(ids);
}
